import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiService } from "../services/api";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Container from "@mui/material/Container";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { styled } from "@mui/material/styles";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CardActions from "@mui/material/CardActions";
import SitemarkIcon from "../components/landing/SitemarkIcon";
import ColorModeIconDropdown from "../theme/ColorModeIconDropdown";

const TRIAL_MINUTES = 200;
const TRIAL_DAYS = 14;

const RegisterContainer = styled(Stack)(({ theme }) => ({
  minHeight: "100vh",
  padding: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(4),
  },
  "&::before": {
    content: '""',
    display: "block",
    position: "absolute",
    zIndex: -1,
    inset: 0,
    backgroundImage:
      "radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))",
    backgroundRepeat: "no-repeat",
    ...theme.applyStyles("dark", {
      backgroundImage:
        "radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))",
    }),
  },
}));

const PASSWORD_RULES = [
  { re: /.{8,}/, label: "At least 8 characters" },
  { re: /[A-Z]/, label: "One uppercase letter" },
  { re: /[a-z]/, label: "One lowercase letter" },
  { re: /[0-9]/, label: "One number" },
  { re: /[^A-Za-z0-9]/, label: "One special character" },
];

const Register: React.FC = () => {
  const [plans, setPlans] = useState<any[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<string>("");
  const [mode, setMode] = useState<"trial" | "direct">("trial");
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    firstName: "",
    lastName: "",
    companyName: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(false);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await apiService.getPlans();
      if (response.data) {
        setPlans(response.data);
      }
      setLoadingPlans(false);
    } catch (error) {
      console.error("Failed to fetch plans:", error);
      setLoadingPlans(false);
    }
  };

  const selectedPlanData = plans.find((p) => p.id === selectedPlan);

  const trialEndDate = (() => {
    const d = new Date();
    d.setDate(d.getDate() + TRIAL_DAYS);
    return d.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  })();

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else {
      const failedRule = PASSWORD_RULES.find(
        (r) => !r.re.test(formData.password),
      );
      if (failedRule) {
        newErrors.password = failedRule.label + " required";
      }
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    if (!formData.companyName.trim()) {
      newErrors.companyName = "Company name is required";
    }

    if (!selectedPlan) {
      newErrors.plan = "Please select a plan";
    }

    if (!termsAccepted) {
      newErrors.terms = "You must accept the Terms of Service and Privacy Policy to continue";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await apiService.createCheckoutSession({
        planId: selectedPlan,
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        companyName: formData.companyName,
        isTrial: mode === "trial",
      });

      if (!response.data) {
        throw new Error("No session data returned");
      }

      window.location.href = response.data.url;
    } catch (error: any) {
      console.error("Checkout error:", error);
      setErrors({
        submit:
          error.response?.data?.message || "Failed to start checkout process",
      });
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: "" });
    }
  };

  return (
    <>
      <CssBaseline enableColorScheme />
      <RegisterContainer>
        <Box
          sx={{
            position: "absolute",
            top: { xs: 16, sm: 24 },
            right: { xs: 16, sm: 24 },
          }}
        >
          <ColorModeIconDropdown />
        </Box>
        <Container maxWidth="lg" sx={{ py: { xs: 4, sm: 8 } }}>
          <Stack spacing={4} alignItems="center">
            <Box sx={{ textAlign: "center" }}>
              <SitemarkIcon />
              <Typography variant="h3" sx={{ mt: 2, mb: 1 }}>
                Get Started
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Choose how you'd like to start
              </Typography>
            </Box>

            {/* Trial / Direct toggle */}
            <Box sx={{ textAlign: "center" }}>
              <ToggleButtonGroup
                value={mode}
                exclusive
                onChange={(_, val) => val && setMode(val)}
                sx={{ mb: 1 }}
              >
                <ToggleButton value="trial" sx={{ px: 4, py: 1.5 }}>
                  <Stack alignItems="center" spacing={0.25}>
                    <Typography variant="subtitle2" fontWeight={700}>
                      {TRIAL_DAYS}-Day Free Trial
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {TRIAL_MINUTES} min free · card required
                    </Typography>
                  </Stack>
                </ToggleButton>
                <ToggleButton value="direct" sx={{ px: 4, py: 1.5 }}>
                  <Stack alignItems="center" spacing={0.25}>
                    <Typography variant="subtitle2" fontWeight={700}>
                      Subscribe Now
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Full plan · charged today
                    </Typography>
                  </Stack>
                </ToggleButton>
              </ToggleButtonGroup>

              {mode === "trial" && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  You will not be charged until <strong>{trialEndDate}</strong>.
                  {selectedPlanData && (
                    <>
                      {" "}
                      Then ${selectedPlanData.price}/month for{" "}
                      {selectedPlanData.name}.
                    </>
                  )}
                </Typography>
              )}
            </Box>

            {loadingPlans ? (
              <Box sx={{ textAlign: "center", py: 8 }}>
                <Typography>Loading plans...</Typography>
              </Box>
            ) : (
              <>
                {/* Plan cards */}
                <Grid
                  container
                  spacing={3}
                  sx={{
                    alignItems: "center",
                    justifyContent: "center",
                    maxWidth: 1200,
                  }}
                >
                  {plans.map((plan, index) => {
                    const isFeatured = index === 1;
                    const isSelected = selectedPlan === plan.id;
                    return (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }} key={plan.id}>
                        <Card
                          onClick={() => {
                            setSelectedPlan(plan.id);
                            if (errors.plan) setErrors({ ...errors, plan: "" });
                          }}
                          sx={[
                            {
                              p: 2,
                              display: "flex",
                              flexDirection: "column",
                              gap: 4,
                              height: "100%",
                              cursor: "pointer",
                              outline: isSelected ? "2px solid" : "none",
                              outlineColor: isFeatured
                                ? "hsl(210, 100%, 70%)"
                                : "primary.main",
                            },
                            isFeatured &&
                              ((theme) => ({
                                border: "none",
                                background:
                                  "radial-gradient(circle at 50% 0%, hsl(220, 20%, 35%), hsl(220, 30%, 6%))",
                                boxShadow: `0 8px 12px hsla(220, 20%, 42%, 0.2)`,
                                ...theme.applyStyles("dark", {
                                  background:
                                    "radial-gradient(circle at 50% 0%, hsl(220, 20%, 20%), hsl(220, 30%, 16%))",
                                  boxShadow: `0 8px 12px hsla(0, 0%, 0%, 0.8)`,
                                }),
                              })),
                          ]}
                        >
                          <CardContent>
                            <Box
                              sx={[
                                {
                                  mb: 1,
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  gap: 2,
                                },
                                isFeatured
                                  ? { color: "grey.100" }
                                  : { color: "" },
                              ]}
                            >
                              <Typography component="h3" variant="h6">
                                {plan.name}
                              </Typography>
                              {isFeatured && (
                                <Chip
                                  icon={<AutoAwesomeIcon />}
                                  label="Most Popular"
                                />
                              )}
                            </Box>
                            <Box
                              sx={[
                                {
                                  display: "flex",
                                  alignItems: "baseline",
                                  flexWrap: "nowrap",
                                },
                                isFeatured
                                  ? { color: "grey.50" }
                                  : { color: null },
                              ]}
                            >
                              <Typography
                                component="h3"
                                variant="h2"
                                sx={{ flexShrink: 0 }}
                              >
                                ${Math.round(plan.price)}
                              </Typography>
                              <Typography
                                component="h3"
                                variant="h6"
                                sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
                              >
                                &nbsp; per month
                              </Typography>
                            </Box>

                            {mode === "trial" && (
                              <Typography
                                variant="caption"
                                sx={[
                                  { display: "block", mt: 0.5 },
                                  isFeatured
                                    ? { color: "grey.300" }
                                    : { color: "text.secondary" },
                                ]}
                              >
                                Free for {TRIAL_DAYS} days, then $
                                {Math.round(plan.price)}
                                /mo
                              </Typography>
                            )}

                            <Divider
                              sx={{
                                my: 2,
                                opacity: 0.8,
                                borderColor: "divider",
                              }}
                            />
                            {plan.features.map(
                              (feature: string, idx: number) => (
                                <Box
                                  key={idx}
                                  sx={{
                                    py: 1,
                                    display: "flex",
                                    gap: 1.5,
                                    alignItems: "center",
                                  }}
                                >
                                  <CheckCircleRoundedIcon
                                    sx={[
                                      { width: 20 },
                                      isFeatured
                                        ? { color: "primary.light" }
                                        : { color: "secondary.main" },
                                    ]}
                                  />
                                  <Typography
                                    variant="subtitle2"
                                    component="span"
                                    sx={[
                                      isFeatured
                                        ? { color: "grey.50" }
                                        : { color: null },
                                    ]}
                                  >
                                    {feature}
                                  </Typography>
                                </Box>
                              ),
                            )}
                          </CardContent>
                          <CardActions>
                            <Button
                              fullWidth
                              variant="contained"
                              sx={{
                                backgroundImage: "none",
                                backgroundColor: "hsl(210, 98%, 42%)",
                                color: "white",
                                border: "none",
                                boxShadow: "none",
                                "&:hover": {
                                  backgroundImage: "none",
                                  backgroundColor: "hsl(210, 100%, 35%)",
                                  boxShadow: "none",
                                },
                              }}
                            >
                              {isSelected ? "✓ Selected" : "Select Plan"}
                            </Button>
                          </CardActions>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>

                {errors.plan && (
                  <Typography color="error" variant="body2">
                    {errors.plan}
                  </Typography>
                )}

                {/* Account form */}
                <Card sx={{ maxWidth: 600, width: "100%" }} variant="outlined">
                  <CardContent sx={{ p: 4, pt: 2 }}>
                    <Typography variant="h5" sx={{ mb: 0.5 }}>
                      Account Information
                    </Typography>
                    {mode === "trial" ? (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        {TRIAL_MINUTES} minutes free during your trial · no
                        charge until {trialEndDate}
                      </Typography>
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        Full plan access starts immediately after payment
                      </Typography>
                    )}
                    <Box
                      component="form"
                      onSubmit={handleSubmit}
                      noValidate
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      <TextField
                        fullWidth
                        label="Company Name"
                        error={Boolean(errors.companyName)}
                        helperText={errors.companyName}
                        value={formData.companyName}
                        onChange={(e) =>
                          handleInputChange("companyName", e.target.value)
                        }
                      />

                      <Grid container spacing={2}>
                        <Grid size={{ xs: 6 }}>
                          <TextField
                            fullWidth
                            label="First Name"
                            error={Boolean(errors.firstName)}
                            helperText={errors.firstName}
                            value={formData.firstName}
                            onChange={(e) =>
                              handleInputChange("firstName", e.target.value)
                            }
                          />
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <TextField
                            fullWidth
                            label="Last Name"
                            error={Boolean(errors.lastName)}
                            helperText={errors.lastName}
                            value={formData.lastName}
                            onChange={(e) =>
                              handleInputChange("lastName", e.target.value)
                            }
                          />
                        </Grid>
                      </Grid>

                      <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        error={Boolean(errors.email)}
                        helperText={errors.email}
                        value={formData.email}
                        onChange={(e) =>
                          handleInputChange("email", e.target.value)
                        }
                      />

                      <TextField
                        fullWidth
                        label="Password"
                        type={showPassword ? "text" : "password"}
                        error={Boolean(errors.password)}
                        helperText={
                          errors.password ||
                          "Min 8 chars · uppercase · lowercase · number · special character"
                        }
                        value={formData.password}
                        onChange={(e) =>
                          handleInputChange("password", e.target.value)
                        }
                        slotProps={{
                          input: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => setShowPassword((v) => !v)}
                                  size="small"
                                  sx={{ ml: -5 }}
                                  aria-label={
                                    showPassword
                                      ? "Hide password"
                                      : "Show password"
                                  }
                                >
                                  {showPassword ? (
                                    <EyeSlashIcon
                                      style={{ width: 20, height: 20 }}
                                    />
                                  ) : (
                                    <EyeIcon
                                      style={{ width: 20, height: 20 }}
                                    />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          },
                        }}
                      />

                      <TextField
                        fullWidth
                        label="Confirm Password"
                        type={showConfirmPassword ? "text" : "password"}
                        error={Boolean(errors.confirmPassword)}
                        helperText={errors.confirmPassword}
                        value={formData.confirmPassword}
                        onChange={(e) =>
                          handleInputChange("confirmPassword", e.target.value)
                        }
                        slotProps={{
                          input: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() =>
                                    setShowConfirmPassword((v) => !v)
                                  }
                                  size="small"
                                  sx={{ ml: -5 }}
                                  aria-label={
                                    showConfirmPassword
                                      ? "Hide password"
                                      : "Show password"
                                  }
                                >
                                  {showConfirmPassword ? (
                                    <EyeSlashIcon
                                      style={{ width: 20, height: 20 }}
                                    />
                                  ) : (
                                    <EyeIcon
                                      style={{ width: 20, height: 20 }}
                                    />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          },
                        }}
                      />

                      {/* Terms acceptance */}
                      <Box>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={termsAccepted}
                              onChange={(e) => {
                                setTermsAccepted(e.target.checked);
                                if (errors.terms) setErrors({ ...errors, terms: "" });
                              }}
                              size="small"
                              color="primary"
                              sx={{
                                borderRadius: 0.5,
                                "& .MuiSvgIcon-root": { borderRadius: 0.5 },
                                ...(errors.terms && { color: "error.main" }),
                              }}
                            />
                          }
                          label={
                            <Typography variant="body2" sx={{ color: errors.terms ? "error.main" : "text.secondary" }}>
                              I have read and agree to the{" "}
                              <Link
                                href="/terms"
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                              >
                                Terms of Service
                              </Link>
                              {" "}and{" "}
                              <Link
                                href="/privacy"
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                              >
                                Privacy Policy
                              </Link>
                              . I confirm that I have obtained all necessary consents to record the
                              calls I intend to upload.
                            </Typography>
                          }
                          sx={{ alignItems: "flex-start", "& .MuiCheckbox-root": { pt: 0.5 } }}
                        />
                        {errors.terms && (
                          <Typography color="error" variant="caption" sx={{ display: "block", mt: 0.5, ml: 4 }}>
                            {errors.terms}
                          </Typography>
                        )}
                      </Box>

                      {errors.submit && (
                        <Typography color="error" variant="body2">
                          {errors.submit}
                        </Typography>
                      )}

                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        size="large"
                        disabled={loading || loadingPlans}
                      >
                        {loading
                          ? "Processing..."
                          : mode === "trial"
                            ? `Start ${TRIAL_DAYS}-Day Free Trial`
                            : "Continue to Payment"}
                      </Button>

                      <Typography variant="body2" sx={{ textAlign: "center" }}>
                        Already have an account?{" "}
                        <Link
                          onClick={() => navigate("/login")}
                          sx={{ cursor: "pointer" }}
                        >
                          Sign in
                        </Link>
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>

                <Typography variant="body2" color="text.secondary">
                  Secure payment powered by Stripe
                </Typography>
              </>
            )}
          </Stack>
        </Container>
      </RegisterContainer>
    </>
  );
};

export default Register;
